import type { MinuteRange } from '@reditelji/shared';
import { create } from 'zustand';

/** What a drop at the pointer would do, drawn on the row before it happens. */
export interface DropPreview extends MinuteRange {
  postId: string;
  postCode: string;
  name: string;
  /** `replace` takes somebody's row; the rest are the candidate groups a drop can land in. */
  tone: 'rec' | 'warn' | 'flag' | 'replace';
  /** The one thing worth saying about it: the first warning, or that it fits. */
  label: string;
}

const same = (a: DropPreview | null, b: DropPreview | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.postId === b.postId &&
    a.fromMin === b.fromMin &&
    a.toMin === b.toMin &&
    a.tone === b.tone &&
    a.label === b.label &&
    a.name === b.name);

/**
 * Its own store, not a field of `useUi`: a drag changes it many times a
 * second, and most of the timeline reads `useUi` whole, so every change there
 * would render the page again. Here only the row it lands on and the drag
 * chip listen, and an unchanged preview is not written at all.
 */
export const useDropPreview = create<{
  preview: DropPreview | null;
  setPreview: (p: DropPreview | null) => void;
}>((set, get) => ({
  preview: null,
  setPreview: (p) => {
    if (!same(get().preview, p)) set({ preview: p });
  },
}));
